import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { Delete, Edit, Search, Add, Lock, LockOpen } from "@mui/icons-material";
import { Formik } from "formik";
import {
  FiscalYear,
  createFiscalYear,
  deleteFiscalYear,
  fetchFiscalYears,
  openFiscalYearsForm,
  setFiscalYear,
  updateFiscalYear,
  closeFiscalYear,
  reopenFiscalYear,
} from "../../../redux/slices/accounting";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { format } from "date-fns";
import useAuth from "../../../hooks/useAuth";
import { DatePicker } from "@mui/x-date-pickers";
import CustomSnackbar from "../SnackBar";
import useSnackbar from "../SnackbarHook";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { fi } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toDateOnlyString } from "../../../utils/dateUtils";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Button = styled(MuiButton)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

function FiscalYearForm({
  onComplete,
  handleOpen,
}: {
  onComplete: (success: boolean) => void;
  handleOpen: () => void;
}) {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { fiscalYear } = useAppSelector((state) => state.accounting);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const handleSubmit = async (
    values: any,
    { resetForm, setErrors, setStatus, setSubmitting }: any
  ) => {
    try {
      const payload = {
        ...values,
        from: toDateOnlyString(values.from),
        to: toDateOnlyString(values.to),
      };

      if (fiscalYear?.id != null) {
        dispatch(
          updateFiscalYear({
            id: fiscalYear.id!,
            fiscalYear: payload,
            associationId,
          })
        );
      } else {
        dispatch(createFiscalYear({ fiscalYear: payload, associationId }));
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      dispatch(openFiscalYearsForm());
      onComplete(true);
    } catch (e) {
      setStatus({ sent: false });
      setErrors({ submit: "Error" });
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={
        fiscalYear
          ? {
              from: fiscalYear.from,
              to: fiscalYear.to,
              active: fiscalYear.active,
            }
          : {
              from: new Date(),
              to: new Date(),
              active: true,
            }
      }
      // validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <Card mb={6}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tilikausi
            </Typography>
            <form onSubmit={handleSubmit}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={fi}
              >
                <Grid container spacing={6} mt={1}>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Alkaen"
                      value={values.from}
                      inputFormat="dd.MM.yyyy"
                      onChange={(val) => {
                        setFieldValue("from", val);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="outlined" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Päättyy"
                      value={values.to}
                      inputFormat="dd.MM.yyyy"
                      onChange={(val) => {
                        setFieldValue("to", val);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="outlined" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      {/* This is the `active` flag: which year is the current
                          one. Closing a year is the lock action in the list. */}
                      <FormLabel component="legend">
                        Nykyinen tilikausi
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-label="nykyinen tilikausi"
                        name="active"
                        value={values.active.toString()}
                        onChange={(event) => {
                          setFieldValue(
                            "active",
                            event.target.value === "true"
                          );
                        }}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio />}
                          label="Kyllä"
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio />}
                          label="Ei"
                          disabled={!fiscalYear}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      mt={3}
                      mr={2}
                    >
                      Tallenna
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      mt={3}
                      onClick={handleOpen}
                    >
                      Peruuta
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </form>
          </CardContent>
        </Card>
      )}
    </Formik>
  );
}

function FiscalYearList(props: any) {
  const { currentUser } = useAuth();
  const { status } = useAppSelector((state) => state.accounting);
  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tilikaudet
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Add />}
          onClick={() => props.handleEditFiscalYear(undefined)}
        >
          Lisää tilikausi
        </Button>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Alku Pvm</TableCell>
              <TableCell>Loppu Pvm</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : props.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Ei tilikausia
                </TableCell>
              </TableRow>
            ) : (
              props.rows.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {format(new Date(row.from), "dd.MM.yyyy")}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.to), "dd.MM.yyyy")}
                  </TableCell>
                  <TableCell>
                    {row.closedAt
                      ? `Päätetty ${format(
                          new Date(row.closedAt),
                          "dd.MM.yyyy"
                        )}`
                      : row.active
                      ? "Avoin (nykyinen)"
                      : "Avoin"}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Näytä tiedot">
                      <IconButton size="small" onClick={() => {}}>
                        <Search fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Muokkaa">
                      <IconButton
                        size="small"
                        onClick={() => props.handleEditFiscalYear(row)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!row.closedAt && new Date(row.to) < new Date() && (
                      <Tooltip title="Päätä tilikausi">
                        <IconButton
                          size="small"
                          onClick={() => props.handleClose(row)}
                        >
                          <Lock fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.closedAt && (
                      <Tooltip title="Avaa tilikausi uudelleen">
                        <IconButton
                          size="small"
                          onClick={() => props.handleReopen(row)}
                        >
                          <LockOpen fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {currentUser?.isSuperAdmin && (
                      <Tooltip title="Poista">
                        <IconButton
                          size="small"
                          onClick={() => props.handleDelete(row.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function FiscalYears() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { fiscalYears, open } = useAppSelector((state) => state.accounting);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const notify = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleOpen = () => {
    dispatch(openFiscalYearsForm());
  };

  const handleEditFiscalYear = (fiscalYear: FiscalYear) => {
    dispatch(setFiscalYear(fiscalYear));
    dispatch(openFiscalYearsForm());
  };

  const handleDelete = (id: number) => {
    // TODO confirm
    dispatch(deleteFiscalYear(id));
  };

  /**
   * Closing writes into equity and locks the period, so it says what it will do
   * before doing it. Server refusals arrive as 409 with a Finnish message that
   * already names what to fix — shown verbatim rather than replaced.
   */
  const handleClose = async (row: any) => {
    const period = `${format(new Date(row.from), "dd.MM.yyyy")}\u2013${format(
      new Date(row.to),
      "dd.MM.yyyy"
    )}`;
    if (
      !window.confirm(
        `Päätetäänkö tilikausi ${period}?\n\n` +
          "Tilikauden tulos siirretään tilille 200 Edell. tilik. voitto (tappio), " +
          "eikä tilikaudelle voi enää kirjata."
      )
    ) {
      return;
    }
    try {
      await dispatch(
        closeFiscalYear({ associationId, fiscalYearId: row.id })
      ).unwrap();
      notify("Tilikausi päätetty.", "success");
    } catch (error: any) {
      notify(error?.message ?? "Tilikauden päättäminen epäonnistui.", "error");
    }
  };

  const handleReopen = async (row: any) => {
    if (
      !window.confirm(
        "Avataanko tilikausi uudelleen?\n\nTilinpäätöskirjaus poistetaan."
      )
    ) {
      return;
    }
    try {
      await dispatch(
        reopenFiscalYear({ associationId, fiscalYearId: row.id })
      ).unwrap();
      notify("Tilikausi avattu uudelleen.", "success");
    } catch (error: any) {
      notify(error?.message ?? "Tilikauden avaaminen epäonnistui.", "error");
    }
  };

  useEffect(() => {
    dispatch(fetchFiscalYears(associationId));
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Osakkaat" />
      <Typography variant="h3" gutterBottom display="inline">
        Tilikaudet
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Tilikaudet</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {open ? (
        <FiscalYearForm
          handleOpen={handleOpen}
          onComplete={(success: any) => {
            setProgress(100);
            if (success) {
              setSnackbarMessage("Tilikausi tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa tilikautta.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
        />
      ) : (
        <FiscalYearList
          rows={fiscalYears}
          handleEditFiscalYear={handleEditFiscalYear}
          handleDelete={handleDelete}
          handleClose={handleClose}
          handleReopen={handleReopen}
          openForm={open}
        />
      )}
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default FiscalYears;
